import { reactive, ref, watch } from "vue";

function debounce(fn, delayMs) {
  let t = null;
  return (...args) => {
    if (t) clearTimeout(t);
    t = setTimeout(() => fn(...args), delayMs);
  };
}

/**
 * fetcher: async ({ page, perPage, search, filters }) => ({ data, pagination })
 */
export function useServerList(fetcher, options = {}) {
  const {
    initialPage = 1,
    initialPerPage = 20,
    initialSearch = "",
    initialFilters = {},
    searchDebounceMs = 300,
  } = options;

  const items = ref([]);
  const pagination = ref({ total_pages: 1 });
  const loading = ref(false);
  const error = ref(null);

  const query = reactive({
    page: initialPage,
    perPage: initialPerPage,
    search: initialSearch,
    filters: { ...initialFilters },
  });

  const refresh = async () => {
    loading.value = true;
    error.value = null;
    try {
      const result = await fetcher({
        page: query.page,
        perPage: query.perPage,
        search: query.search,
        filters: query.filters,
      });

      items.value = result?.data ?? [];
      pagination.value = result?.pagination ?? { total_pages: 1 };
    } catch (e) {
      error.value = e;
      items.value = [];
      pagination.value = { total_pages: 1 };
    } finally {
      loading.value = false;
    }
  };

  const refreshDebounced = debounce(() => refresh(), searchDebounceMs);

  watch(
    () => query.page,
    () => refresh()
  );

  watch(
    () => query.perPage,
    () => {
      query.page = 1;
      refresh();
    }
  );

  watch(
    () => query.filters,
    () => {
      query.page = 1;
      refresh();
    },
    { deep: true }
  );

  watch(
    () => query.search,
    () => {
      query.page = 1;
      refreshDebounced();
    }
  );

  return { items, pagination, loading, error, query, refresh };
}